import React, {Component} from 'react'
import propTypes from 'prop-types'
import Icon from 'components/icon'
import {prefix} from 'helpers/component'
import {SelectField} from 'components/selectField'
import {isNil} from 'lodash'
import {t} from "helpers/language"
import themeVars from 'theme/themeVars'

const getClassName = prefix('pivotTable-pageControl-')

const DISPLAY_PAGES = 5

class PageControl extends Component {
  constructor(props) {
    super(props)
    this.state = {
      goto: '1',
    }
  }

  goToChange = (e) => {
    this.setState({goto: e.target.value})
  }

  goToKeyDown = (e) => {
    switch (e.keyCode) {
      case 13: {  // ENTER
        this.onGoToSubmit()
        break
      }
      case 27: {  // ESC
        e.target.blur()
        break
      }
    }
  }

  onGoToSubmit = () => {
    const {goto} = this.state

    let page = parseInt(goto)
    if (page === 0) return this.goToBlur()
    if (!isNaN(page)) {
      page--
      const totalPages = Math.ceil(this.props.totalRecords / this.props.pageSize)
      if (page < 0) page = 0
      if (page >= totalPages) page = Math.max(0, totalPages - 1)
      this.props.onChange(page)
    }
    this.goToBlur()
  }

  goToBlur = () => {
    this.setState({goto: ''})
  }

  getPageButton = (page, key, label, hint, selected = false, enabled = true, className) => {
    const {onChange} = this.props

    const classes = ['page']
    classes.push(enabled ? 'page-enabled' : 'page-disabled')
    if (enabled && selected) classes.push('page-selected')
    if (!isNil(className)) classes.push(className)

    return (<div key={key} title={hint} className={getClassName(classes)} onClick={() => {
      if (enabled) onChange(page)
    }}>{label}</div>)
  }

  getPageSizes = () => {
    return [50, 100, 250, 500].map(pageSize => {
      return {
        key: pageSize,
        value: pageSize,
        label: `${pageSize}`,
      }
    })
  }

  getTotalPages = (totalRecords, pageSize) =>
      Math.ceil(totalRecords / pageSize)

  onUpdatePageSize = (pageSize) => {
    const {currentPage, totalRecords, onUpdatePageSize} = this.props
    const newPageCount = this.getTotalPages(totalRecords, pageSize)
    const newPage = Math.min(currentPage, newPageCount - 1)
    onUpdatePageSize(pageSize, newPage)
  }

  getPageButtons = (startPage, endPage, totalPages) => {
    const pages = []
    const {currentPage} = this.props

    const backButtonsEnabled = (currentPage > 0)
    const forwardButtonsEnabled = (currentPage < totalPages - 1)

    pages.push(this.getPageButton(
        0,
        'first',
        <Icon name="skip_to_start_filled" size={16} color={backButtonsEnabled ? null : themeVars.palette.disabledColor}/>,
        'First Page',
        false,
        backButtonsEnabled,
        'first-page'))

    pages.push(this.getPageButton(
        currentPage - 1,
        'prev',
        <Icon name="reverse_play_filled" size={16} color={backButtonsEnabled ? null : themeVars.palette.disabledColor}/>,
        'Previous Page',
        false,
        backButtonsEnabled,
        'previous-page'))

    for (let i = startPage; i < endPage; i++) {
      pages.push(this.getPageButton(i, i, i + 1, 'Jump to Page', i === currentPage))
    }

    pages.push(this.getPageButton(
        currentPage + 1,
        'next',
        <Icon name="play_filled" size={16} color={forwardButtonsEnabled ? null : themeVars.palette.disabledColor}/>,
        'Next Page',
        false,
        forwardButtonsEnabled,
        'next-page'))

    pages.push(this.getPageButton(
        totalPages - 1,
        'last',
        <Icon name="end_filled" size={16} color={forwardButtonsEnabled ? null : themeVars.palette.disabledColor}/>,
        'Last Page',
        false,
        forwardButtonsEnabled,
        'last-page'))

    return pages
  }

  render() {
    const {
      pageSize,
      totalRecords,
      pluralNoun,
      footerLeft,
      footerRight,
      showRowSelector,
    } = this.props
    let {currentPage} = this.props

    const totalPages = this.getTotalPages(totalRecords, pageSize)
    if (currentPage < 0) currentPage = 0
    if (totalPages > 0 && currentPage >= totalPages) currentPage = totalPages - 1

    const pageShift = Math.ceil((DISPLAY_PAGES - 1) / 2)
    let startPage = currentPage - pageShift
    let endPage = currentPage + pageShift + 1
    if (startPage < 0) {
      startPage = 0
      endPage = startPage + DISPLAY_PAGES
    }
    if (endPage > totalPages) {
      endPage = totalPages
      startPage = endPage - DISPLAY_PAGES
      if (startPage < 0) startPage = 0
    }

    let startRecord = (currentPage * pageSize) + 1
    let endRecord = startRecord + pageSize
    if (startRecord > totalRecords) startRecord = totalRecords
    if (endRecord > totalRecords + 1) endRecord = totalRecords + 1

    let pagesElm, jumpElm
    if (startRecord > 1 || endRecord < totalRecords) {
      pagesElm = (
          <div className={getClassName('pages')}>
            {this.getPageButtons(startPage, endPage, totalPages)}
          </div>
      )
      jumpElm = (
          <div className={getClassName('goto')} title="Jump to Page">
            <div className={getClassName('gotoIcon')} onClick={this.onGoToSubmit}>
              <Icon name="right_filled" size={16}/>
            </div>
            <input
                ref={e => {
                  this.goto = e
                }}
                className={getClassName('gotoInput')}
                value={this.state.goto}
                onChange={this.goToChange}
                onKeyDown={this.goToKeyDown}/>
          </div>
      )
    }

    let label
    totalPages > 1
        ? label = (
            <div className={getClassName('label')}>
              Showing {startRecord} - {Math.max(endRecord - 1, 0)} of {totalRecords} {pluralNoun}
            </div>)
        : label = (
            <div className={getClassName('label')}>
              {t(`Showing {1} {2}`, totalRecords, pluralNoun)}
            </div>)

    const rowSelector = (
        <div className={getClassName('page-size')}>
          <div className={getClassName('page-size-label')}>
            {t('Show rows')}
          </div>
          <SelectField
              key={'page-size'}
              clearable={false}
              showDivider={false}
              value={pageSize}
              options={this.getPageSizes()}
              onChange={v => this.onUpdatePageSize(v.value)}
          />
        </div>
    )

    return (
        <div className={getClassName('root')}>
          {showRowSelector && rowSelector}
          {footerLeft}
          {pagesElm}
          {jumpElm}
          {label}
          {footerRight}
        </div>
    )
  }
}

PageControl.propTypes = {
  pageSize: propTypes.number.isRequired,
  currentPage: propTypes.number.isRequired,
  totalRecords: propTypes.number,
  pluralNoun: propTypes.string.isRequired,
  footerLeft: propTypes.any,
  footerRight: propTypes.any,
  onChange: propTypes.func.isRequired,
  onUpdatePageSize: propTypes.func.isRequired,
  showRowSelector: propTypes.bool.isRequired,
}

export default PageControl
